import { createSelector } from "reselect";

import { selectSalesIntelligenceState } from "app/pages/sales-intelligence/store/selectors";
import { selectIsContactsVisible } from "app/pages/sales-intelligence/sub-modules/contacts-tab/store/selectors";
import { createStatePropertySelector } from "app/pages/workspace/sales/helpers";

import { countMissingRequiredColumns, makeSelectRequiredColumns, makeSelectMappedFields } from "../../../utils/export";
import { getChangedTabNames, getTabsWithEmptyFields } from "../../../utils/utils";
import type { ExportName, ExportSetting, HubspotExportName } from "../../type";
import { selectCurrentExportTab } from "../common";
import { selectHubspotLoadingState } from "../hubspotSelectors";
import {
    getAccountsSimilarwebFieldOptions,
    getHubspotContactsSimilarwebFieldOptions,
} from "../exportSimilarwebFieldOptions";

const management = createStatePropertySelector(selectSalesIntelligenceState("management"));
const hsExport = createStatePropertySelector(management("hubspotExport"));

export const selectHsSettings = hsExport("settings");
export const selectHsIsLoadingSettings = hsExport("isLoadingSettings");
const selectHsPrevSettings = hsExport("prevSettings");
export const selectHsDeletableField = hsExport("deletableField");
export const selectHsExportingTo = hsExport("exportingDomainsTo");
export const selectHsNotification = hsExport("notification");
export const selectHsIsSavingSettings = hsExport("isSavingSettings");

export const selectHsCurrentSettings = createSelector(
    selectCurrentExportTab,
    selectHsSettings,
    (tabName: "accounts" | "contacts", allSettings): ExportSetting[] => allSettings[tabName] || [],
);

const makeSelectHsMappings = (recordName: HubspotExportName) =>
    createSelector(selectHsSettings, (settings) => settings[recordName]);

const selectHsAccountsMappings = makeSelectHsMappings("accounts");
const selectHsContactsMappings = makeSelectHsMappings("contacts");

const selectHsAccountsMappedFields = makeSelectMappedFields(selectHsAccountsMappings);
const selectHsContactsMappedFields = makeSelectMappedFields(selectHsContactsMappings);

const selectHsColumns = hsExport("columns");

export const makeSelectHsColumns = (recordName: HubspotExportName) =>
    createSelector(selectHsColumns, (columns) => columns[recordName]);

export const selectHsAccountsColumns = makeSelectHsColumns("accounts");
export const selectHsContactsColumns = makeSelectHsColumns("contacts");

const selectHsRequiredAccountsColumns = makeSelectRequiredColumns(selectHsAccountsColumns);
const selectHsRequiredContactsColumns = makeSelectRequiredColumns(selectHsContactsColumns);

export const selectHsAccountsMissingRequiredFieldsCount = createSelector(
    selectHsRequiredAccountsColumns,
    selectHsAccountsMappedFields,
    countMissingRequiredColumns,
);

export const selectHsContactsMissingRequiredFieldsCount = createSelector(
    selectHsRequiredContactsColumns,
    selectHsContactsMappedFields,
    countMissingRequiredColumns,
);

export const selectHsMissingRequiredFieldsCount = createSelector(
    selectHsAccountsMissingRequiredFieldsCount,
    selectHsContactsMissingRequiredFieldsCount,
    (accounts, contacts) => ({ accounts, contacts, leads: 0, companyAsLeads: 0 }),
);

export const selectHsPrevSettingsByCurrentType = createSelector(
    selectCurrentExportTab,
    selectHsPrevSettings,
    (tabName: "accounts" | "contacts", allPrevSettings): ExportSetting[] => allPrevSettings[tabName] || [],
);

export const selectHsTotalSettings = createSelector(selectHsCurrentSettings, (settings): number => settings.length);

export const selectHsTabsWithEmptyFields = createSelector(
    selectHsSettings,
    selectIsContactsVisible,
    (allExportSettings, isContactsVisible): ExportName[] =>
        getTabsWithEmptyFields({
            allExportSettings,
            isContactsVisible,
        }),
);

export const selectHsIsTabsWithEmptyFields = createSelector(
    selectHsTabsWithEmptyFields,
    (tabsWithEmptyFields) => !!tabsWithEmptyFields.length,
);

export const selectHsSettingsChangedTabNames = createSelector(
    selectHsSettings,
    selectHsPrevSettings,
    getChangedTabNames,
);

export const selectIsHsSettingsChanged = createSelector(
    selectHsSettingsChangedTabNames,
    (tabNames) => !!tabNames.length,
);

export const selectHsNotSelectedSettings = getAccountsSimilarwebFieldOptions;

export const selectHsContactsNotSelectedSettings = getHubspotContactsSimilarwebFieldOptions;

export const selectHsIsColumnsLoading = hsExport("isColumnsLoading");
export const selectIsLoadingHsExportPreferences = createSelector(
    selectHsIsColumnsLoading,
    selectHsIsLoadingSettings,
    selectHubspotLoadingState,
    (isLoadingColumns, isLoadingSettings, isLoadingStatus) => {
        return isLoadingColumns || isLoadingSettings || isLoadingStatus;
    },
);
